import React from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/system";
import cx from "classnames";
import { InfoTitle } from "../InfoTitle";
import { ErrorMessage } from "../Notifications";
import { ICONS } from "../Icons";

const StyledFieldDescriptionWrapper = styled("div", {
  shouldForwardProp: prop => prop !== "showInLineError"
})(({ theme, showInLineError }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "start",
  fontSize: "12px",
  fontWeight: "400",
  color: "#757575",
  marginTop: "4px",
  flexWrap: showInLineError ? "nowrap" : "unset",
  "& .informationDisclaimer": {
    display: "flex",
    alignItems: "start",
    whiteSpace: "pre-line",
    marginRight: "auto",
    gap: "8px",
    "& div:first-child": {
      marginTop: 0
    }
  },
  "& .errorContainer": {
    "&.warningContainer, .errorContainer": {
      marginTop: "0px"
    }
  },
  "& .counter": {
    display: "flex",
    alignItems: "center"
  },
  "& .counterValue": {
    marginRight: "0.2rem"
  },
  "& .counterSeparator": {
    margin: "0 2px"
  },
  "& .counterMax": {
    marginLeft: "0.2rem"
  },
  "& .warningContainer": {
    color: "#F59E0B"
  }
}));

export const FieldDescription = ({
  title,
  fieldValueLength = 0,
  fieldMaxLength,
  showInLineError = false,
  errorMessage = "",
  showWarning = false,
  isError = false,
  warningDesc = "",
  showCounter = true,
  id
}) => {
  // Use only the MUI hook for responsive design, avoid direct window access
  const errorId = id ? `${id}-error` : "field-description-error";
  const warningId = id ? `${id}-warning` : "field-description-warning";
  const counterId = id ? `${id}-counter` : "field-description-counter";

  let DescriptionContent = (
    <div className="informationDisclaimer">
      {title && <InfoTitle iconName={ICONS.information} title={title} />}
    </div>
  );

  if (showInLineError && errorMessage && isError) {
    DescriptionContent = (
      <ErrorMessage error={errorMessage} classes={{ error: "errorContainer" }} id={errorId} />
    );
  }

  if (showInLineError && showWarning && !isError) {
    DescriptionContent = (
      <p className={cx("errorContainer", "warningContainer")} id={warningId} role="alert">
        {warningDesc}
      </p>
    );
  }

  return (
    <StyledFieldDescriptionWrapper
      showInLineError={showInLineError}
      data-testid="fieldDescriptionWrapper"
    >
      {DescriptionContent}
      {showCounter && fieldMaxLength && (
        <div
          className="counter"
          data-testid="Field-counter"
          id={counterId}
          aria-live="polite"
          aria-label={`${fieldValueLength} of ${fieldMaxLength} characters used`}
        >
          <span className="counterValue">{fieldValueLength}</span>
          <span className="counterSeparator">/</span>
          <span className="counterMax">{fieldMaxLength}</span>
        </div>
      )}
    </StyledFieldDescriptionWrapper>
  );
};

FieldDescription.propTypes = {
  title: PropTypes.string,
  fieldValueLength: PropTypes.number,
  fieldMaxLength: PropTypes.number,
  showInLineError: PropTypes.bool,
  errorMessage: PropTypes.string,
  showWarning: PropTypes.bool,
  isError: PropTypes.bool,
  warningDesc: PropTypes.string,
  showCounter: PropTypes.bool,
  id: PropTypes.string
};

export default FieldDescription;
